import React from 'react';
import { ActionIcon, Popover, Code, Text, Stack } from '@mantine/core';
import { IconQuestionMark } from '@tabler/icons-react';
import { QUESTION_TYPE_IDS } from '../../PatientDashboard/PatientJournal/QuestionTypes';

const RESPONSE_MESSAGES_EXAMPLE = `
  "responseMessages": [
    {
      "triggerValue": "yes",
      "messageEn": "Message shown when value matches",
      "messageFr": "Message affiché quand la valeur correspond",
      "severity": "info | warning"
    }
  ]`;

const OPTIONS_JSON_FORMATS: Record<number, string> = {
    [QUESTION_TYPE_IDS.YES_NO]: `{${RESPONSE_MESSAGES_EXAMPLE}
}`,

    [QUESTION_TYPE_IDS.YES_NO_WITH_SCALE]: `{${RESPONSE_MESSAGES_EXAMPLE}
}
// triggerValue supports: "yes", "no", ">=6", "<5", ">3", "<=8"
// Matches against yes/no and scale value`,

    [QUESTION_TYPE_IDS.YES_NO_WITH_COUNT]: `{
  "min": 0,
  "max": 999,
  "unit": {
    "en": "times",
    "fr": "fois"
  },
  "placeholder": {
    "en": "Enter number",
    "fr": "Entrez le nombre"
  },${RESPONSE_MESSAGES_EXAMPLE}
}`,

    [QUESTION_TYPE_IDS.CONDITIONAL_MULTISELECT]: `{
  "primaryLabelEn": "Yes - (describe...)",
  "primaryLabelFr": "Oui - (décrire...)",
  "secondaryLabelEn": "No",
  "secondaryLabelFr": "Non",
  "primaryOptionsEn": ["High", "Medium", "Low"],
  "primaryOptionsFr": ["Élevé", "Moyen", "Bas"],
  "secondaryOptionsEn": ["None", "Other"],
  "secondaryOptionsFr": ["Aucun", "Autre"],${RESPONSE_MESSAGES_EXAMPLE}
}`,

    [QUESTION_TYPE_IDS.MULTI_SELECT]: `{
  "allowMultiple": true,
  "maxSelections": 3,
  "options": [
    { "valueEn": "Option A", "valueFr": "Option A" },
    { "valueEn": "Option B", "valueFr": "Option B" }
  ],${RESPONSE_MESSAGES_EXAMPLE}
}`,

    [QUESTION_TYPE_IDS.FREE_TEXT]: `{
  "maxLength": 500,${RESPONSE_MESSAGES_EXAMPLE}
}`,

    [QUESTION_TYPE_IDS.SCALE]: `{${RESPONSE_MESSAGES_EXAMPLE}
}
// triggerValue supports: ">=6", "<5", ">3", "<=8"`,

    [QUESTION_TYPE_IDS.YES_NO_WITH_NUMERIC_SCALE]: `{
  "min": 0,
  "max": 10,
  "step": 1,
  "labelMin": {
    "en": "No pain",
    "fr": "Aucune douleur"
  },
  "labelMax": {
    "en": "Worst pain imaginable",
    "fr": "Pire douleur imaginable"
  },${RESPONSE_MESSAGES_EXAMPLE}
}
// min/max/step/labelMin/labelMax are all optional; defaults to 0-10, step 1, no labels
// triggerValue supports: "yes", "no", ">=6", "<5", ">3", "<=8"
// Matches against yes/no and scale value`,

    [QUESTION_TYPE_IDS.TOGGLE]: `{
  "labelEn": "I took my medication",
  "labelFr": "J'ai pris mon médicament",${RESPONSE_MESSAGES_EXAMPLE}
}
// Default labels: On/Off if not specified`,
};

interface OptionsJsonHelperProps {
    questionTypeID: number;
}

export const OptionsJsonHelper: React.FC<OptionsJsonHelperProps> = ({ questionTypeID }) => {
    const format = OPTIONS_JSON_FORMATS[questionTypeID];

    if (!format) return null;

    return (
        <Popover width={480} position="bottom-start" withArrow shadow="md">
            <Popover.Target>
                <ActionIcon
                    variant="filled"
                    color="blue"
                    size="xs"
                    radius="xl"
                    onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                    }}
                >
                    <IconQuestionMark size={14} />
                </ActionIcon>
            </Popover.Target>
            <Popover.Dropdown>
                <Stack gap="xs">
                    <Text size="sm" fw={600}>JSON Format</Text>
                    <Code block style={{ whiteSpace: 'pre', fontSize: '12px', maxHeight: 400, overflow: 'auto' }}>
                        {format.trim()}
                    </Code>
                </Stack>
            </Popover.Dropdown>
        </Popover>
    );
};
